import { Box, Divider } from '@mui/material';
import React from 'react';
import {
  GuardianBasicInfoSchema,
  GuardianInfoSchema,
  familyBasicInfoSchema,
  familyInfoSchema
} from '../config/schema';
import { Control, UseFormSetValue } from 'react-hook-form';
import Icons from '../../../components/icons';
import {
  HelperText,
  ImagePreview,
  StudentProfileWrapper,
  UploadButton
} from '../styled';
import { DataGrid } from '@mui/x-data-grid';
import FormRenderer from '../../../components/FormRenderer/FormRenderer';
import { getSiblingColumns } from '../utils/SiblingColumns';
import { familyTabTypes } from '../types';
import { StudentDetailProps } from '../types/studentDetailType';
import { uploadPhoto } from '../utils/uploadPhoto';
import { SnackbarSeverityType } from '../../../enums';
import { dataGridSx } from '../../common/styled/DataGrid';
import pallet from '../../../layouts/pallet';
import tableColumnUtils from '../../../utils/tableColumnUtils';

interface FamilyTabContentProps {
  activeTab: familyTabTypes;
  control: Control<StudentDetailProps, any, StudentDetailProps>;
  setValue: UseFormSetValue<StudentDetailProps>;
  showToast: (message: string, severity: SnackbarSeverityType) => void;
}

const FamilyTabContent: React.FC<FamilyTabContentProps> = ({
  activeTab,
  control,
  setValue,
  showToast
}) => {
  const formData = control._getWatch();

  const uploadParentPhoto = async (file: File | null) => {
    if (!file) return;

    const isGuardian = activeTab === 'guardianDetails';
    const id = isGuardian
      ? formData?.motherDetails?._id || formData?.fatherDetails?._id
      : formData?.[activeTab]?._id;

    const response = await uploadPhoto(file, id, isGuardian);

    if (response && response.errors) {
      showToast(
        response?.errors?.[0].message || 'Failed to upload profile',
        SnackbarSeverityType.error
      );
      return;
    }

    if (response) {
      setValue(
        isGuardian
          ? 'studentDetails.guardianInfo.profile'
          : (`${activeTab}.profile` as any),
        response.uploadedPath
      );
      showToast('Successfully uploaded profile', SnackbarSeverityType.success);
    }
  };

  const handleUploadButtonClick = () => {
    const input = document.getElementById(
      `family-profile-upload-${activeTab}`
    ) as HTMLInputElement;
    input?.click();
  };

  switch (activeTab) {
    case 'siblingDetails':
      return (
        <DataGrid
          rowHeight={50}
          rows={formData.siblingDetails || []}
          columns={getSiblingColumns()}
          getRowId={(row) => {
            const random4Digit = Math.floor(1000 + Math.random() * 9000);
            return `${row.enrollmentNo || ''}_${
              row.name || ''
            }_${random4Digit}`;
          }}
          disableRowSelectionOnClick
          pageSizeOptions={tableColumnUtils.PAGE_SIZE_OPTIONS}
          initialState={tableColumnUtils.DATA_GRID_INITIAL_STATE}
          showToolbar
          slotProps={{
            toolbar: {
              printOptions: { disableToolbarButton: true },
              showQuickFilter: true
            }
          }}
          sx={{
            ...dataGridSx(pallet),
            backgroundColor: 'white',
            '& .MuiDataGrid-virtualScroller': {
              overflowY: 'auto'
            },
            maxHeight: 'calc(100% - 100px)'
          }}
        />
      );
    default:
      return (
        <Box>
          <StudentProfileWrapper>
            <ImagePreview
              src={
                activeTab !== 'guardianDetails'
                  ? formData?.[activeTab]?.profile
                  : formData.studentDetails?.guardianInfo?.profile
              }
              alt='Profile'
              style={{ width: '148px', height: '148px' }}
            />
            <Box sx={{ flex: 1, width: '100%' }}>
              <input
                type='file'
                accept='.jpg,.jpeg,.png,.gif,image/jpeg,image/png,image/gif'
                style={{ display: 'none' }}
                id={`family-profile-upload-${activeTab}`}
                onChange={async (e) => {
                  const file = e.target.files?.[0] || null;
                  if (!file) return;

                  await uploadParentPhoto(file);
                }}
              />
              <UploadButton
                startIcon={<Icons.ImageFillPrimary />}
                onClick={handleUploadButtonClick}
              >
                Upload Photo
              </UploadButton>
              <HelperText>Allowed JPG, GIF or PNG. Max size of 800K</HelperText>
              <FormRenderer
                control={control}
                setValue={setValue}
                schema={
                  activeTab === 'guardianDetails'
                    ? GuardianBasicInfoSchema
                    : familyBasicInfoSchema(activeTab)
                }
              />
            </Box>
          </StudentProfileWrapper>
          <Divider sx={{ mb: 3, mt: 1 }} />
          <FormRenderer
            control={control}
            setValue={setValue}
            schema={
              activeTab === 'guardianDetails'
                ? GuardianInfoSchema
                : familyInfoSchema(activeTab)
            }
          />
        </Box>
      );
  }
};

export default FamilyTabContent;
